import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// Components
import { Loader } from '../../../inputs/Loader';
import InputText from '../../../inputs/InputText';
import DropDownInput from '../../../inputs/DropDown/DropDownInput';
import MultiSelectDropdown from '../../../inputs/DropDown/MultiSelectDropdown';
import { InputTextArea } from '../../../inputs/InputTextArea';
import { DateTime } from '../../../inputs/DateTime';
import { ArrowDirectionButton } from '../../../inputs/buttons/ArrowDirectionButton';
import { AlertIcon } from '../../../../assets/icons/AlertIcon';

// Hooks
import {
  useRoadDirectionLabels,
  useRoadNamesById,
  useRoadPkRange,
} from '../../../../hooks/useRoadDirectionLabels';

// Data
import { getLaneOptions } from '../../../../utils/catalogLabels';

// Utils
import { roadPkRangeAFormulario } from '../../../../utils/pkValidation';
import { parsePk, pkFormatter } from '../../../../utils/transform';
import { registerPk } from '../../../../utils/pkField';

// Types
import { ModalProps } from './types';
import { UNSPECIFIC } from '../../../../types/utils';
import { WorkOrderDirection } from '../../../../types/workOrder';

// Schema
import { buildEditSegmentSchema, EditSegmentFormData } from './schema';

// Styles
import {
  ActionButton,
  ButtonRow,
  ContainerForm,
  ContainerLoader,
  DatesGrid,
  FieldsGrid,
  InlineError,
  ModalContainerStyled,
  PkFieldWrap,
  Section,
  Text,
  TextAreaWrap,
  Title,
  CrossingNotice,
  CrossingNoticeIcon,
} from './styles';
import { startRoadIdOf } from '../../../../utils/roadStretches';

/**
 * EditSegmentModal (spec 2026-07-31) — corrección de GESTIÓN de un tramo ya
 * cerrado: PK inicial, PK final, sentido, carril, horas y descripción.
 *
 * No es un gesto de campo: se abre desde la tabla de Tramos del detalle del RV
 * (botón por fila, solo para SuperAdmin/Coex/Capataz/RoomTeam y con el
 * recorrido pausado o pendiente de validación). Por eso NO prerrellena por GPS
 * como los modales del cronómetro — parte de los valores guardados.
 *
 * 2026-08-10: migrado a **react-hook-form + yup**, como sus tres hermanos del
 * ciclo de patrulla y como los otros 135 formularios de la app. Antes llevaba
 * siete `useState` y un único mensaje al pie, con los códigos de error
 * clasificados A MANO para decidir qué campo pintar en rojo — y esa lista
 * fallaba en silencio en cuanto alguien añadía un código sin clasificarlo.
 * Ahora la regla vive en el schema y react-hook-form ata cada error a su campo.
 *
 * La validación se hace DOS veces a propósito: aquí (inline, para que el
 * usuario vea el problema sin esperar al servidor) y en el backend, que es la
 * autoridad. Las dos usan las mismas reglas y la misma clave i18n del mensaje.
 */
export const EditSegmentModal = ({
  onClickClose,
  data,
}: ModalProps): JSX.Element => {
  const { t } = useTranslation([
    'pages',
    'common',
    'forms',
    'errors',
    // `validation` para el «campo obligatorio» del selector de carretera: con
    // prefijo explícito el namespace tiene que estar CARGADO, no basta con que
    // exista la clave.
    'validation',
  ]);
  const theme = useTheme();
  const isLoading = data?.isLoading ?? false;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const segment = data?.segment;
  const segments = useMemo(() => data?.segments ?? [], [data?.segments]);

  // Spec 2026-08-15: el mismo modal sirve a dos audiencias. En `operator` el
  // Tablet ve PK y horas —para saber qué tramo toca— pero bloqueados, y su
  // schema apaga esas reglas (si no, un tramo histórico con el PK fuera de la
  // carretera le impediría guardar un cambio de carril).
  const mode = data?.mode ?? 'management';
  const esOperario = mode === 'operator';
  const schema = useMemo(() => buildEditSegmentSchema(mode), [mode]);

  // mc53 — la carretera del tramo, corregible a mano.
  //
  // El servidor lo aceptaba desde el primer día (`CorrectRouteSegment` lleva
  // `roadId` y `roadStretches`), pero el formulario no los mandaba: un cruce
  // mal anotado no se podía deshacer desde ninguna pantalla, ni corregir la vía
  // de un tramo que se inició en la equivocada.
  const routeRoadIds = useMemo(() => data?.roadIds ?? [], [data?.roadIds]);
  const roadNames = useRoadNamesById(routeRoadIds);
  const roadOptions = useMemo(
    () => routeRoadIds.map((id) => ({ value: id, label: roadNames[id] || id })),
    [routeRoadIds, roadNames]
  );
  // Las vías por las que el tramo fue, tal y como están guardadas: sus trozos
  // si cruzó, o su carretera si no. Es lo que llega marcado.
  const viasDelTramo = useMemo(() => {
    const trozos = data?.segment?.roadStretches ?? [];
    if (trozos.length > 0) return trozos.map((t) => t.roadId);
    const propia = data?.segment ? startRoadIdOf(data.segment) : null;
    return propia ? [propia] : [];
  }, [data?.segment]);

  const [selectedRoadIds, setSelectedRoadIds] = useState<string[]>(viasDelTramo);
  const roadsChanged =
    selectedRoadIds.length !== viasDelTramo.length ||
    selectedRoadIds.some((id, i) => id !== viasDelTramo[i]);

  // 🔴 Dejar el selector vacío se bloquea AQUÍ, no mandándolo al servidor.
  //
  // El servidor lo rechaza (`SR_ROADS_EMPTY`) y es la autoridad, pero esta
  // corrección pasa por la COLA OFFLINE: sin cobertura el envío se encola, el
  // usuario ve «guardado», y al reproducirse el servidor lo rechaza y la cola
  // descarta la operación — con ella se irían también las horas y la
  // descripción que sí eran válidas. Mandar algo que sabemos condenado es
  // perder el resto del parte.
  //
  // Es la doctrina que este modal ya declara: validar dos veces, aquí para que
  // el usuario lo vea al momento y allí porque la API la puede llamar
  // cualquiera y la cola reproduce envíos viejos.
  //
  // Solo cuando el tramo TENÍA carretera: hay tramos antiguos sin ninguna, y a
  // esos no se les puede exigir rellenarla para corregirles una hora.
  const roadsCleared = viasDelTramo.length > 0 && selectedRoadIds.length === 0;

  // 🔴 Aviso, no bloqueo: quedarse con UNA vía en un tramo que tiene un cruce
  // registrado lo borra. El cruce pudo ponerlo el rastro GPS, que es prueba
  // directa; la persona puede tener razón igualmente —ella iba en el coche—
  // pero tiene que enterarse de qué está pisando.
  const borraraElCruce =
    (data?.segment?.roadStretches?.length ?? 0) > 1 &&
    selectedRoadIds.length === 1;

  const directionLabels = useRoadDirectionLabels(data?.roadId);

  // 🔴 Un tramo que cruzó tiene los extremos en vías DISTINTAS, así que cada PK
  // se valida contra la suya: el inicial contra la de salida y el final contra
  // la de llegada. Con un solo rango para los dos, uno sobraba siempre y el
  // tramo NO SE PODÍA CORREGIR — «El PK no es válido» hiciera lo que hiciera el
  // usuario (2026-08-24). Sin cruce las dos son la misma y esto es lo de antes.
  const viaInicio = segment ? startRoadIdOf(segment) : undefined;
  const roadPkRange = useRoadPkRange(viaInicio ?? data?.roadId);
  const endRoadPkRange = useRoadPkRange(data?.roadId);
  const laneOptions = getLaneOptions(t);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<EditSegmentFormData>({
    resolver: yupResolver(schema),
    // Modo POR DEFECTO a propósito (valida al enviar y, a partir de ahí, en
    // vivo). Es lo que hacen los otros 35 formularios de la app; los únicos 3
    // que lo cambian son los de autenticación, con `onTouched`.
    //
    // Se probó `onChange` y se descartó: pinta el campo en rojo MIENTRAS se
    // teclea —a medio escribir «203 + 100» el valor todavía no tiene formato
    // válido— y eso es ruido en un modal que abre con datos correctos. El
    // comportamiento que se buscaba (que el error se vaya solo al corregir,
    // sin volver a pulsar Guardar) ya lo da el `reValidateMode` por defecto.
    defaultValues: {
      pkInit: segment ? pkFormatter(segment.pkInitKm, segment.pkInitMeter) : '',
      pkEnd:
        segment?.pkEndKm != null && segment?.pkEndMeter != null
          ? pkFormatter(segment.pkEndKm, segment.pkEndMeter)
          : '',
      direction: segment?.direction ?? '',
      lane: segment?.lane ?? '',
      startDate: segment?.startDate ?? '',
      endDate: segment?.endDate ?? '',
      pauseCauseDetail: segment?.pauseCauseDetail ?? '',
      segments,
      segmentId: segment?.id,
      roadId: segment ? (startRoadIdOf(segment) ?? null) : null,
      roadIds: segment ? (segment.roadId ? [segment.roadId] : []) : [],
    },
  });

  /**
   * 🔴 La carretera del formulario sigue al SELECTOR.
   *
   * El validador de solapes corta por carretera, y sin esto compararía siempre
   * contra la vía guardada: cambiar de vía en el modal no quitaría el aviso
   * hasta guardar, que es justo el gesto que tiene que responder al momento.
   */
  useEffect(() => {
    setValue('roadId', selectedRoadIds[0] ?? null, { shouldValidate: false });
    setValue('roadIds', selectedRoadIds, { shouldValidate: true });
  }, [selectedRoadIds, setValue]);

  /**
   * Siembra los extremos de la carretera para que el `.test('pk-validation')`
   * tenga contra qué comparar. Va por `setValue` porque `useRoadPkRange`
   * resuelve ASÍNCRONO: con el detalle abierto en frío la carretera llega
   * después de montar el modal. Mismo gesto que `NewRoute`.
   */
  useEffect(() => {
    const extremos = roadPkRangeAFormulario(roadPkRange);
    setValue('pkInitRoad', extremos.pkInitRoad, { shouldValidate: true });
    setValue('pkEndRoad', extremos.pkEndRoad);
  }, [roadPkRange, setValue]);

  // Los de la vía donde ACABA. Solo son distintos si el tramo cruzó; si no,
  // el schema cae a los de arriba y no cambia nada.
  useEffect(() => {
    const extremos = roadPkRangeAFormulario(endRoadPkRange);
    setValue('endRoadPkInit', extremos.pkInitRoad, { shouldValidate: true });
    setValue('endRoadPkEnd', extremos.pkEndRoad);
  }, [endRoadPkRange, setValue]);

  // Mapping idéntico a StartTimerModal/ChangeDirectionModal.
  const directionOptions: Record<string, string> = {
    top: WorkOrderDirection.TOP,
    down: WorkOrderDirection.DOWN,
    both: WorkOrderDirection.BOTH,
    [UNSPECIFIC]: UNSPECIFIC,
  };

  const onSubmit = useCallback(
    async (formData: EditSegmentFormData) => {
      if (!data?.onConfirm || !segment || isSubmitting) return;
      const init = parsePk(formData.pkInit);
      const end = parsePk(formData.pkEnd);
      // El operario no edita los PK: si un tramo histórico trae uno que no
      // parsea, esta guarda le impediría guardar un cambio de carril. Para él
      // los PK viajan igual en el payload, pero la página los ignora — su
      // mutation ni siquiera tiene dónde ponerlos.
      if (!esOperario && (!init || !end)) return;
      // Defensa: el error ya se pinta bajo el selector en cuanto se queda
      // vacío, pero el submit no puede depender de que el usuario lo lea.
      if (roadsCleared) return;

      setIsSubmitting(true);
      try {
        await data.onConfirm({
          id: segment.id,
          pkInitKm: init?.km ?? segment.pkInitKm,
          pkInitMeter: init?.meter ?? segment.pkInitMeter,
          pkEndKm: end?.km ?? segment.pkEndKm ?? 0,
          pkEndMeter: end?.meter ?? segment.pkEndMeter ?? 0,
          direction: formData.direction,
          startDate: formData.startDate,
          endDate: formData.endDate,
          lane: formData.lane,
          pauseCauseDetail: formData.pauseCauseDetail ?? '',
          // Solo si el operador la ha tocado: `undefined` = no tocar (Apollo no
          // serializa las claves sin valor, así que no llega al servidor).
          //
          // 🔴 Propiedad EXPLÍCITA y no un spread condicional: TypeScript no
          // comprueba lo que sale de un `...(cond ? {…} : {})` —ni el nombre
          // del campo ni su tipo—, así que con el spread una errata viajaba
          // hasta el navegador sin que nadie se enterara. Comprobado.
          //
          // Se manda la INTENCIÓN —por qué vías fue— y el servidor calcula los
          // trozos: la bifurcación sale de la malla, que vive allí.
          roadIds: roadsChanged ? selectedRoadIds : undefined,
        });
        onClickClose();
      } catch {
        // El toast del error ya lo emitió el hook; el modal se queda abierto
        // para corregir o cancelar (mismo patrón que ForceCloseRouteModal).
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      data,
      esOperario,
      isSubmitting,
      onClickClose,
      segment,
      roadsChanged,
      roadsCleared,
      selectedRoadIds,
    ]
  );

  return (
    <ModalContainerStyled
      width={60}
      onClickClose={onClickClose}
      fill={theme.colors.black}
    >
      {isSubmitting && (
        <ContainerLoader>
          <Loader />
        </ContainerLoader>
      )}
      <ContainerForm onSubmit={handleSubmit(onSubmit)} isLoading={isSubmitting}>
        <AlertIcon />

        {/* Sin subtítulo (feedback usuario 2026-08-05). Ocupaba dos líneas en
            tablet vertical y era lo último que impedía que el modal entrase
            sin scroll; además solo repetía en prosa los campos que el propio
            formulario ya enseña etiquetados justo debajo. La clave
            `editSegment.subtitle` se queda en los locales, sin uso. */}
        <Title variant="semiBold">
          {t('pages:surveillance.editSegment.title')}
        </Title>

        {/* mc53 — la carretera del tramo. Solo con más de una en el recorrido:
            con una sola no hay nada que elegir y el modal queda igual que
            siempre. Va ARRIBA por el mismo motivo que en el de iniciar tramo:
            manda sobre lo de abajo, porque los PK se validan contra ELLA.
            Cambiarla declara que el tramo NO cruzó — es la marcha atrás de un
            cruce mal anotado. Reusa las claves de siempre, sin i18n nueva. */}
        {!esOperario && roadOptions.length > 1 && (
          <Section>
            <Text size="S">{t('forms:workOrder.road')}</Text>
            <MultiSelectDropdown
              id="edit-segment-road-select"
              inputSize="L"
              typeDropDown="Default"
              placeholder={t('common:labels.select')}
              options={roadOptions}
              selectedValues={selectedRoadIds}
              onChangeSelected={setSelectedRoadIds}
              borderError={roadsCleared}
              error={roadsCleared ? t('validation:required') : undefined}
            />
            {borraraElCruce && (
              <CrossingNotice role="alert">
                <CrossingNoticeIcon aria-hidden="true">⚠</CrossingNoticeIcon>
                <span>
                  {t('pages:surveillance.editSegment.crossingWillBeRemoved')}
                </span>
              </CrossingNotice>
            )}
          </Section>
        )}

        <FieldsGrid>
          <Section>
            <Text size="S">{t('forms:workOrder.pkInitial')}</Text>
            <PkFieldWrap>
              <InputText
                {...registerPk(register, setValue, 'pkInit')}
                typeInput="normal"
                inputSize="S"
                placeholder={t('common:pkExample')}
                borderError={!!errors.pkInit}
                error={
                  errors.pkInit?.message ? t(errors.pkInit.message) : undefined
                }
                data-testid="edit-segment-pk-init"
                disabled={esOperario}
              />
            </PkFieldWrap>
          </Section>

          <Section>
            <Text size="S">{t('forms:workOrder.pkFinal')}</Text>
            <PkFieldWrap>
              <InputText
                {...registerPk(register, setValue, 'pkEnd')}
                typeInput="normal"
                inputSize="S"
                placeholder={t('common:pkExample')}
                borderError={!!errors.pkEnd}
                error={
                  errors.pkEnd?.message ? t(errors.pkEnd.message) : undefined
                }
                data-testid="edit-segment-pk-end"
                disabled={esOperario}
              />
            </PkFieldWrap>
          </Section>
        </FieldsGrid>

        <Section>
          <Text size="S">{t('forms:workOrder.direction')}</Text>
          <ArrowDirectionButton
            directions={['down', 'both', 'top', UNSPECIFIC]}
            hasUnspecific
            // OJO: ArrowDirectionButton elige el icono de 'both' a partir de
            // ESTE nombre (case 'both' de su renderIcon). Con cualquier otro
            // valor el botón del medio sale en BLANCO. Tiene que ser
            // 'direction', igual que sus hermanos del RV. No alimenta ningún
            // valor: el sentido sale del estado vía onChangeDirection, y el
            // agrupado de radios queda acotado al <form> de este modal.
            name="direction"
            defaultValue={segment?.direction ?? ''}
            directionLabels={directionLabels}
            onChangeDirection={(dir) =>
              setValue('direction', directionOptions[dir] ?? '', {
                shouldValidate: true,
              })
            }
          />
        </Section>

        <Section>
          <Text size="S">{t('forms:workOrder.lane')}</Text>
          <Controller
            name="lane"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DropDownInput
                id="edit-segment-lane"
                inputSize="L"
                typeDropDown="Default"
                placeholder={t('common:labels.select')}
                options={laneOptions}
                selectedOption={value}
                onChangeSelected={(option) => onChange(option?.value ?? '')}
              />
            )}
          />
        </Section>

        <DatesGrid>
          <Section>
            <Controller
              name="startDate"
              control={control}
              render={({ field: { onChange, value } }) => (
                <DateTime
                  label={t('pages:surveillance.editSegment.startDate')}
                  labelHour={t('forms:workOrder.startHour')}
                  value={value}
                  initialDate={value}
                  toDate={new Date()}
                  onChangeDate={(v) => onChange(v ?? '')}
                  borderError={!!errors.startDate}
                  error={
                    errors.startDate?.message
                      ? t(errors.startDate.message)
                      : undefined
                  }
                  data-testid="edit-segment-start"
                  isDisabled={esOperario}
                />
              )}
            />
          </Section>
          <Section>
            <Controller
              name="endDate"
              control={control}
              render={({ field: { onChange, value } }) => (
                <DateTime
                  label={t('pages:surveillance.editSegment.endDate')}
                  labelHour={t('forms:workOrder.endHour')}
                  value={value}
                  initialDate={value}
                  toDate={new Date()}
                  onChangeDate={(v) => onChange(v ?? '')}
                  borderError={!!errors.endDate}
                  data-testid="edit-segment-end"
                  isDisabled={esOperario}
                />
              )}
            />
          </Section>
        </DatesGrid>

        {/* Descripción del tramo (2026-08-10) = `pauseCauseDetail`. Desde que
            solo se pide al cerrar, es el único texto libre del tramo, así que
            se corrige aquí junto al resto en vez de obligar a encontrarlo
            detrás del chip de causa. Vaciarla la borra. La CAUSA sigue
            cambiándose desde el chip: ese camino además dispara la cadena de
            la Actuación básica, que aquí no queremos. */}
        <Section>
          <Text size="S">{t('surveillance.pauseCauseDetail')}</Text>
          <TextAreaWrap>
            <Controller
              name="pauseCauseDetail"
              control={control}
              render={({ field }) => (
                <InputTextArea
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value)}
                  placeholder={t('surveillance.pauseModal.detailPlaceholder')}
                  disabled={false}
                />
              )}
            />
          </TextAreaWrap>
        </Section>

        {/* Hueco para errores que NO cuelgan de un campo. Hoy no hay ninguno
            —todos los ata react-hook-form a su campo y se pintan en rojo ahí—
            pero se conserva el testid para quien lo busque y por si el backend
            devuelve alguno que no sepamos colocar. */}
        {errors.root?.message && (
          <InlineError size="S" data-testid="edit-segment-error">
            {t(errors.root.message)}
          </InlineError>
        )}

        <ButtonRow>
          <ActionButton
            type="button"
            text={t('common:buttons.cancel')}
            variant="secondary"
            onClick={onClickClose}
            disabled={isLoading || isSubmitting}
          />
          <ActionButton
            type="submit"
            text={t('pages:surveillance.editSegment.confirm')}
            disabled={isLoading || isSubmitting}
            data-testid="edit-segment-confirm"
          />
        </ButtonRow>
      </ContainerForm>
    </ModalContainerStyled>
  );
};

export default EditSegmentModal;
